import React, { useState, useEffect } from 'react';
import { Button, Table, Space, Input, Form, Typography, Modal, message, Popconfirm, Layout, Row, Col, Card, Select } from 'antd';
import { SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, LockOutlined } from '@ant-design/icons';
import {baseDataService, userService} from '../../services';
import {
  UserSearchRequest,
  UserSearchResponseItem,
  UserCreateItem,
  UserUpdateItem,
  UserDeleteItem,
  DropdownResponse
} from "../../model";
import { getPageSizeOptions, resetPaginationByTotal } from 'utils/pageUtil';

const { Title } = Typography;
const { Content } = Layout;
const { Option } = Select;

// 使用者設定頁面元件
const UserPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserSearchResponseItem[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentUser, setCurrentUser] = useState<UserSearchResponseItem | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [userRole, setUserRole] = useState<DropdownResponse[] | null>([]);

  useEffect(() => {

    const fetchUserRole = async () => {
      // 載入使用者權限清單
      const role = await baseDataService.getDropdown({paramType: 'user_role'});
      setUserRole(role);
    }

    fetchUserRole();
  }, []);

  // 搜尋使用者
  const handleSearch = async (values: any) => {
    setLoading(true);
    try {
      // 構建搜尋請求
      const searchRequest: UserSearchRequest = {
        userAccount: values.userAccount || '',
        userRole: values.userRole || ''
      };

      // 調用API搜尋使用者
      const result = await userService.search(searchRequest);

      if (result) {
        setUsers(result);
        setTotalRecords(result.length);
        // 重置到第一頁
        setPagination(resetPaginationByTotal(result.length));

        // 如果沒有返回數據，顯示通知
        if (result.length === 0) {
          message.info('查無資料');
        }
      } else {
        // 如果返回的結果為null（可能是由於網絡錯誤或其他問題）
        setUsers([]);
        setTotalRecords(0);
        message.error('查詢失敗，請稍後重試');
      }
    } catch (error) {
      console.error('搜尋使用者失敗:', error);
      message.error('搜尋失敗，請稍後重試');
      setUsers([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  // 處理分頁、篩選和排序變更
  const handleTableChange = (paginationConfig: any, filters: any, sorter: any) => {
    const newPagination = {
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    };
    
    setPagination(newPagination);
  };

  // 重置表單
  const handleReset = () => {
    form.resetFields();
  };

  // 打開新增使用者對話框
  const showAddModal = () => {
    setCurrentUser(null);
    editForm.resetFields();
    setIsModalVisible(true);
  };

  // 打開編輯使用者對話框
  const showEditModal = (user: UserSearchResponseItem) => {
    setCurrentUser(user);
    editForm.setFieldsValue({
      userAccount: user.userAccount,
      userPassword: user.userPassword,
      userRole: user.userRole
    });
    setIsModalVisible(true);
  };

  // 關閉對話框
  const handleCancel = () => {
    setIsModalVisible(false);
  };

  // 保存使用者（新增或編輯）
  const handleSave = async () => {
    try {
      const values = await editForm.validateFields();
      setLoading(true);
      
      const userAccount = localStorage.getItem('userAccount') || 'system';

      if (currentUser) {
        // 編輯現有使用者
        const updateItem: UserUpdateItem = {
          usersId: currentUser.usersId,
          userAccount: values.userAccount,
          userPassword: values.userPassword,
          userRole: values.userRole,
          modifiedBy: userAccount
        };

        const updateResult = await userService.update({
          users: [updateItem]
        });

        if (updateResult && updateResult.updatedCount > 0) {
          message.success('使用者更新成功');
          setIsModalVisible(false);
          // 刷新使用者列表
          handleSearch(form.getFieldsValue());
        } else {
          message.error('使用者更新失敗');
        }
      } else {
        // 創建新使用者
        const createItem: UserCreateItem = {
          userAccount: values.userAccount,
          userPassword: values.userPassword,
          userRole: values.userRole,
          createdBy: userAccount
        };

        const createResult = await userService.create({
          users: [createItem]
        });

        if (createResult && createResult.createdCount > 0) {
          message.success('新增使用者成功');
          setIsModalVisible(false);
          // 刷新使用者列表
          handleSearch(form.getFieldsValue());
        } else {
          message.error('新增使用者失敗');
        }
      }
    } catch (error) {
      console.error('保存使用者失敗:', error);
      message.error('操作失敗，請稍後重試');
    } finally {
      setLoading(false);
    }
  };

  // 刪除使用者
  const handleDelete = async (usersId: number) => {
    setLoading(true);
    try {
      const deleteItem: UserDeleteItem = { usersId };
      const deleteResult = await userService.delete({
        userIds: [deleteItem]
      });

      if (deleteResult && deleteResult.deletedCount > 0) {
        message.success('使用者刪除成功');
        // 刷新使用者列表
        handleSearch(form.getFieldsValue());
      } else {
        message.error('使用者刪除失敗');
      }
    } catch (error) {
      console.error('刪除使用者失敗:', error);
      message.error('刪除失敗，請稍後重試');
    } finally {
      setLoading(false);
    }
  };

  // 轉換使用者角色為文字顯示
  const renderUserRole = (role: string) => {
    switch(role) {
      case '1':
        return 'level 1';
      case '2':
        return 'level 2';
      case '3':
        return 'level 3';
      case '4':
        return 'level 4';
      case '5':
        return 'level 5';
      default:
        return `角色 ${role}`;
    }
  };

  // 表格列定義
  const columns = [
    {
      title: '序號',
      dataIndex: 'index',
      key: 'index',
      render: (_: any, __: any, index: number) => index + 1
    },
    {
      title: '使用者帳號',
      dataIndex: 'userAccount',
      key: 'userAccount'
    },
    {
      title: '使用者權限',
      dataIndex: 'userRole',
      key: 'userRole',
      render: (role: string) => renderUserRole(role)
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: UserSearchResponseItem) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => showEditModal(record)}
            title="編輯"
          />
          <Popconfirm
            title="確定要刪除此使用者嗎？"
            okText="確定"
            cancelText="取消"
            onConfirm={() => handleDelete(record.usersId)}
          >
            <Button
                danger
                icon={<DeleteOutlined />}
                title="刪除"
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Layout className="user-page">
      <Content>
        {/* 頁面標題 */}
        <Row justify="center" className="mb-lg">
          <Col>
            <Title level={3} className="page-title">
              <span className="page-title-icon">👤</span> 使用者設定
            </Title>
          </Col>
        </Row>

        {/* 搜尋表單 */}
        <Card
          title={
            <Space>
              <SearchOutlined />
              <span>搜尋條件</span>
            </Space>
          }
          className="card-content-spacing"
        >
          <Form
            form={form}
            name="userSearch"
            onFinish={handleSearch}
            layout="vertical"
          >
            <Row gutter={[24, 0]}>
              {/* 使用者帳號 */}
              <Col xs={24} md={12}>
                <Form.Item label="使用者帳號" name="userAccount">
                  <Input placeholder="請輸入使用者帳號" />
                </Form.Item>
              </Col>
              
              {/* 使用者權限 */}
              <Col xs={24} md={12}>
                <Form.Item label="使用者權限" name="userRole">
                  <Select placeholder="請選擇使用者權限">
                    {
                      userRole?.map(role => (
                        <Option key={role.paramCode} value={role.paramCode}>
                          {role.paramValue}
                        </Option>
                      ))
                    }
                  </Select>
                </Form.Item>
              </Col>
              
              {/* 按鈕區域 */}
              <Col xs={24}>
                <Form.Item wrapperCol={{ span: 24 }}>
                  <div className="form-button-container">
                    <Button 
                      onClick={handleReset} 
                      icon={<DeleteOutlined />}
                    >
                      清除
                    </Button>
                    <Button 
                      type="primary" 
                      htmlType="submit" 
                      icon={<SearchOutlined />}
                    >
                      查詢
                    </Button>
                  </div>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>

        {/* 搜尋結果區域 */}
        <Card
          title={
            <div className="card-with-actions">
              <span>搜尋結果: {totalRecords} 筆記錄</span>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={showAddModal}
              >
                新增使用者
              </Button>
            </div>
          }
        >
          <Table
            rowKey="usersId"
            dataSource={users}
            columns={columns}
            loading={loading}
            pagination={{
              ...pagination,
              total: totalRecords,
              showSizeChanger: true,
              pageSizeOptions: getPageSizeOptions(totalRecords),
              showTotal: (total) => `共 ${total} 筆`
            }}
            onChange={handleTableChange}
          />
        </Card>

        {/* 新增/編輯使用者對話框 */}
        <Modal
          title={currentUser ? '編輯使用者' : '新增使用者'}
          open={isModalVisible}
          onOk={handleSave}
          onCancel={handleCancel}
          confirmLoading={loading}
          okText="保存"
          cancelText="取消"
        >
          <Form
            form={editForm}
            layout="vertical"
            name="userForm"
          >
            <Form.Item
              name="userAccount"
              label="使用者帳號"
              rules={[{ required: true, message: '請輸入使用者帳號' }]}
            >
              <Input 
                placeholder="請輸入使用者帳號" 
                disabled={!!currentUser} // 編輯時不允許更改帳號
              />
            </Form.Item>
            <Form.Item
              name="userPassword"
              label="使用者密碼"
              rules={[{ required: !currentUser, message: '請輸入使用者密碼' }]}
            >
              <Input.Password 
                placeholder={currentUser ? "不修改請留空" : "請輸入使用者密碼"}
                iconRender={visible => (visible ? <LockOutlined /> : <LockOutlined />)}
              />
            </Form.Item>
            <Form.Item
              name="userRole"
              label="使用者權限"
              rules={[{ required: true, message: '請選擇使用者權限' }]}
            >
              <Select placeholder="請選擇使用者權限">
                <Option value="1">level 1</Option>
                <Option value="2">level 2</Option>
                <Option value="3">level 3</Option>
                <Option value="4">level 4</Option>
                <Option value="5">level 5</Option>
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </Content>
    </Layout>
  );
};

export default UserPage;